'use client';

import { useCallback, useRef, useState } from 'react';

import { Routes } from '@/app/enums';

import {
  ActiveGoodHabitDonationResponse,
  ActiveGoodHabitVoucherResponse,
  Donation,
  Project,
  ProjectPhoto,
  ProjectStateStatusEnum,
  Subproject,
  UserRolesEnum,
} from '@/app/api';
import { SUBPROJECT_SEARCH_PARAM_KEY } from '@/app/constants';
import { useCurrentUser } from '@/app/hooks/useCurrentUser';
import { SideBarContentEnum, useDisplaySettings } from '@/app/pages/ProjectDetail/hooks/useDisplaySettings';
import { useInitialScrollToTabs } from '@/app/pages/ProjectDetail/hooks/useInitialScrollToTabs';
import { useScrollToTabsIfNeeded } from '@/app/pages/ProjectDetail/hooks/useScrollToTabsIfNeeded';
import { useSetSuccessBackground } from '@/app/pages/ProjectDetail/hooks/useSetSuccessBackground';
import { ProjectTabs as ProjectTabsEnum } from '@/app/pages/ProjectDetail/types';

import GoodHabitBarWidget from '@/app/components/GoodHabitBarWidget/GoodHabitBarWidget';
import LanguageSwitch from '@/app/components/LanguageSwitch/LanguageSwitch';
import ProjectPageHeader from '@/app/components/ProjectPageHeader/ProjectPageHeader';
import ProjectsVerificationDialog from '@/app/components/ProjectsVerificationDialog/ProjectsVerificationDialog';
import ShareDialog from '@/app/components/ShareDialog/ShareDialog';
import SubprojectGoodHabitNotification from '@/app/components/SubprojectGoodHabitNotification/SubprojectGoodHabitNotification';
import useAnalytics from '@/app/hooks/useAnalytics';
import { DonationsSideBar } from '@/app/pages/ProjectDetail/components/DonationsSideBar';
import MainProjectPreview from '@/app/pages/ProjectDetail/components/MainProjectPreview';
import { MoreStories } from '@/app/pages/ProjectDetail/components/MoreStories';
import { ProjectClosedWidget } from '@/app/pages/ProjectDetail/components/ProjectClosedWidget';
import ProjectTabs from '@/app/pages/ProjectDetail/components/ProjectTabs';
import { SubProjectTabContent } from '@/app/pages/ProjectDetail/components/SubProjectTabContent';
import { useTabs } from '@/app/pages/ProjectDetail/hooks/useTabs';
import { envs } from '@/app/utils/envs';
import { SupportStoryWidget } from '@/app/components/SupportStoryWidget/SupportStoryWidget';
import { useIsUserInSubProjectBoxReplaceGroup } from '@/app/hooks/useIsUserInSubProjectBoxReplaceGroup';

interface SubprojectDetailProps {
  project: Project;
  subproject: Subproject;
  images: ProjectPhoto[];
  donations?: Donation[];
  moreProjects: Project[];
  activeGoodhabit?: ActiveGoodHabitDonationResponse | ActiveGoodHabitVoucherResponse;
}

const SubprojectDetail = ({
  project,
  images,
  donations,
  moreProjects,
  subproject,
  activeGoodhabit,
}: SubprojectDetailProps) => {
  const projectHeaderRef = useRef<HTMLDivElement>(null);
  const tabsAnchorRef = useRef<HTMLDivElement>(null);
  const [isTabsElementFixed, setIsTabsElementFixed] = useState(false);
  const { currentUser } = useCurrentUser();
  const isUserInSubProjectBoxReplaceGroup = useIsUserInSubProjectBoxReplaceGroup();

  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const tabs = useTabs({ project, subProject: subproject });

  const [isVerificationNoteDialogOpen, setIsVerificationNoteDialogOpen] = useState(false);
  const [activeTabId, setActiveTabId] = useState(ProjectTabsEnum.PROJECT);

  const scrollToTabsIfNeeded = useScrollToTabsIfNeeded(tabsAnchorRef);

  const displaySettings = useDisplaySettings({ project, subproject: subproject });
  const {
    projectDetail: { pushShareModalOpenEvent },
  } = useAnalytics();

  const isClosedOrFinished =
    project.state.status === ProjectStateStatusEnum.Closed || project.state.status === ProjectStateStatusEnum.Finished;

  useSetSuccessBackground({ shouldDisplaySuccessBackground: !!project.state?.actualSuccess && isClosedOrFinished });
  useInitialScrollToTabs(tabsAnchorRef, setActiveTabId);

  const handleShareButtonClick = () => {
    setIsShareDialogOpen(true);
    pushShareModalOpenEvent();
  };

  const handleCloseShareDialog = useCallback(() => {
    setIsShareDialogOpen(false);
  }, [setIsShareDialogOpen]);

  const handleVerificationNoteClick = useCallback(() => {
    setIsVerificationNoteDialogOpen(true);
  }, []);

  const handleCloseVerificationNoteDialog = useCallback(() => {
    setIsVerificationNoteDialogOpen(false);
  }, [setIsVerificationNoteDialogOpen]);

  const handleSelectTab = useCallback(
    (tabId: ProjectTabsEnum) => {
      setActiveTabId(tabId);
      scrollToTabsIfNeeded();
    },
    [setActiveTabId, scrollToTabsIfNeeded],
  );

  const handleToggleTabsElementFixed = useCallback(
    (isFixed: boolean) => {
      setIsTabsElementFixed(isFixed);
    },
    [setIsTabsElementFixed],
  );

  const handleMoreDonorsButtonClick = useCallback(() => {
    setActiveTabId(ProjectTabsEnum.DONORS);
    scrollToTabsIfNeeded();
  }, [scrollToTabsIfNeeded]);

  const handleReadMoreClick = useCallback(() => {
    projectHeaderRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  //TODO: create routes with id as param and replace ids globaly
  const adminDetailLink = `${envs.redirectUrl}/admin/project/${project.id}/subproject/${subproject.id}/detail`;

  return (
    <div className="w-full min-h-[100vh] pb-[40px]">
      {activeGoodhabit && <GoodHabitBarWidget activeGoodhabit={activeGoodhabit} className="mt-[16px] mb-[40px]" />}
      {activeGoodhabit && <SubprojectGoodHabitNotification className="mb-[24px]" />}
      <div className="w-full default-page-max-width mx-auto px-[24px]">
        <LanguageSwitch className="mb-[16px]" />
        {displaySettings.isProjectClosedWidgetDisplayed && (
          <ProjectClosedWidget
            coverImage={project.content.mainPhoto || images[0]}
            project={project}
            moreProjects={moreProjects}
            handleReadMoreClick={handleReadMoreClick}
          />
        )}
        <ProjectPageHeader
          adminDetailLink={adminDetailLink}
          isUserAdmin={currentUser?.roles.includes(UserRolesEnum.Admin)}
          isCommercial={false}
          title={subproject.content.name}
          currentAmountInCents={subproject.statistics.currentAmountInCents}
          goalInCents={subproject.statistics.targetAmountInCents ?? undefined}
          donationsCount={subproject.statistics.donationsCount}
          subProject={subproject}
          numericId={project.numericId}
          // TODO ProjectPhoto & SubprojectPhoto should have the same dto, see https://doniocz.atlassian.net/browse/DON-2092
          // images={[...(subproject.content.mainPhoto ? [subproject.content.mainPhoto] : []), ...images]}
          images={images}
          coverImage={{
            small:
              subproject.content.mainPhoto?.sizes.smallCropped.path ||
              project.content.mainPhoto?.sizes.smallCropped.path ||
              images[0].sizes.smallCropped.path,
            large:
              subproject.content.mainPhoto?.sizes.fullCropped.path ||
              project.content.mainPhoto?.sizes.fullCropped.path ||
              images[0].sizes.fullCropped.path,
          }}
          mainVideoSlug={project.content.texts.mainVideo ?? undefined}
          endsAt={project.timestamps?.scheduledEnding ?? undefined}
          onShareButtonClick={handleShareButtonClick}
          onVerificationNoteClick={handleVerificationNoteClick}
          innerRef={projectHeaderRef}
          isProjectLongTerm={project.settings.isLongTerm}
          activeGoodHabitAmount={activeGoodhabit?.donation.amountInCents}
        />
      </div>

      <div ref={tabsAnchorRef} />
      <ProjectTabs
        projectModeGroup={project.settings.modeGroup}
        tabs={tabs}
        activeTabId={activeTabId}
        donationLink={`/${Routes.DONATION}/${project.numericId}?${SUBPROJECT_SEARCH_PARAM_KEY}=${subproject.id}`}
        isElementFixed={isTabsElementFixed}
        onToggleElementFixed={handleToggleTabsElementFixed}
        onTabChange={handleSelectTab}
      />

      <div className="w-full default-page-max-width mx-auto px-[24px]">
        <div className="flex pb-[32px] lg:gap-[24px] xxl:gap-[124px]">
          <div className="w-full grow lg:max-w-[640px]">
            <SubProjectTabContent project={project} subProject={subproject} activeTabId={activeTabId} />
          </div>
          {displaySettings.sideBarContent === SideBarContentEnum.MESSAGES && (
            <DonationsSideBar donations={donations} onMoreDonorsButtonClick={handleMoreDonorsButtonClick} />
          )}
        </div>
        {displaySettings.isSupportProjectBannerVisible && (
          <SupportStoryWidget
            className="mb-[56px] mt-[72px]"
            activeGoodHabitAmount={activeGoodhabit?.donation.amountInCents}
          />
        )}
        <div className="mb-[70px]">
          <MainProjectPreview
            key={project.id}
            title={project.content?.currentName!}
            image={project.content.mainPhoto ?? undefined}
            href={`/${project.slug}`}
            currentAmountInCents={project.statistics?.currentAmountInCents!}
            targetAmountInCents={project.statistics?.targetAmountInCents!}
            donationsCount={project.statistics?.donationsCount!}
          />
        </div>
        {!!displaySettings.footnoteText && <p className="mt-[16px] mb-[64px]">{displaySettings.footnoteText}</p>}
        {isUserInSubProjectBoxReplaceGroup && (
          <div className="ml-[-24px] md:ml-0 h-[9px] bg-neutral-light w-screen md:w-full mb-[64px]" />
        )}
        <MoreStories projects={moreProjects} title={displaySettings.moreProjectsHeading} />
      </div>
      <ShareDialog
        subProjectId={subproject.id}
        defaultEmailValue={currentUser?.email ?? ''}
        project={project}
        isOpen={isShareDialogOpen}
        onClose={handleCloseShareDialog}
      />
      <ProjectsVerificationDialog isOpen={isVerificationNoteDialogOpen} onClose={handleCloseVerificationNoteDialog} />
    </div>
  );
};

export default SubprojectDetail;
